/**
 * Main admin application with layout and routing.
 * Phase 3: tenant name in header/sidebar, menu filtered by plan.
 * Phase 4: Super-Admin panel under /superadmin/*.
 * Phase 5: Онбординг, приглашения, управление командой.
 */
import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { ConfigProvider, Layout, Menu, Tag, Space, Avatar, Dropdown, theme, Typography, Spin, Switch, Tooltip, Alert, Button, Drawer, Grid } from 'antd';
import type { MenuProps } from 'antd';
import {
  AppstoreOutlined,
  AppstoreAddOutlined,
  ApiOutlined,
  BarChartOutlined,
  CrownOutlined,
  DashboardOutlined,
  GiftOutlined,
  LogoutOutlined,
  OrderedListOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  FileTextOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  BugOutlined,
  MenuOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import AdminBugReportWidget from './components/AdminBugReportWidget';
import RoleRoute, { hasMinimumRole } from './components/RoleRoute';
import { useAuthStore } from './store/authStore';
import { useSAStore } from './store/superAdminStore';
import api from './services/api';

// ── Страницы загружаются из единого реестра pageRegistry.ts ──
import { PAGES, getMenuChildren } from './pageRegistry';
import LazyLoadErrorBoundary from './components/LazyLoadErrorBoundary';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ErrorBoundary from './components/ErrorBoundary';
import DashboardPage from './pages/DashboardPage';
import { HelpCenterPage } from './pages/help/HelpCenterPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ClientProfilePage from './pages/ClientProfilePage_vm';

// ── Super-admin pages ──
import SALoginPage from './pages/superadmin/SALoginPage';
import SADashboardPage from './pages/superadmin/SADashboardPage';
import SATenantsPage from './pages/superadmin/SATenantsPage';
import SARevenuePage from './pages/superadmin/SARevenuePage';
import BillingPage from './pages/billing/BillingPage';
import { useMenuConfigStore, type MenuItemConfig } from './store/menuStore';
import { useThemeStore } from './store/themeStore';
import MenuConfigurator from './components/MenuConfigurator';
import TenantSwitcher from './components/TenantSwitcher';
import './adminShell.css';

// ── Онбординг и приглашения (lazy) ──
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const AcceptInvitePage = React.lazy(() => import('./pages/AcceptInvitePage'));

const { Sider, Content, Header } = Layout;
const { Title, Text } = Typography;

// ── Plan-based menu filtering ──
const PLAN_LEVELS: Record<string, number> = { free: 0, starter: 1, business: 2, enterprise: 3 };
interface MenuItem {
  key: string;
  icon?: React.ReactNode;
  label: string | React.ReactNode;
  minPlan?: string;
  minRole?: string;
  children?: MenuItem[];
}

function toAntMenuItems(items: MenuItem[]): NonNullable<MenuProps['items']> {
  return items.map(({ key, icon, label, children }) => children?.length
    ? { key, icon, label, children: toAntMenuItems(children) }
    : { key, icon, label });
}

const allMenuItems: MenuItem[] = [
  { key: '/', icon: <DashboardOutlined />, label: 'Обзор' },
  { key: '/orders', icon: <ShoppingCartOutlined />, label: 'Заказы' },
  { key: '/catalog', icon: <AppstoreOutlined />, label: 'Меню', children: [
    { key: '/products', icon: <AppstoreOutlined />, label: 'Товары' },
    { key: '/categories', icon: <OrderedListOutlined />, label: 'Категории' },
    { key: '/modifiers', icon: <AppstoreAddOutlined />, label: 'Модификаторы' },
  ] },
  { key: '/clients', icon: <TeamOutlined />, label: 'Клиенты', children: [
    { key: '/clients', label: 'Список клиентов' },
    { key: '/clients/tags', label: 'Метки клиентов' },
    { key: '/clients/imports', label: 'Импорт базы' },
  ] },
  { key: '/marketing', icon: <GiftOutlined />, label: 'Продажи и лояльность', minPlan: 'starter',
    children: getMenuChildren('marketing') },
  { key: '/content', icon: <FileTextOutlined />, label: 'Сайт и контент', minPlan: 'starter',
    children: getMenuChildren('content') },
  { key: '/reports', icon: <BarChartOutlined />, label: 'Отчёты', minPlan: 'business', minRole: 'manager' },
  { key: '/settings', icon: <SettingOutlined />, label: 'Компания',
    children: getMenuChildren('settings') },
  { key: '/integrations', icon: <ApiOutlined />, label: 'Интеграции', minPlan: 'business',
    children: getMenuChildren('integrations') },
  { key: '/billing', icon: <CrownOutlined />, label: 'Подписка', minRole: 'owner' },
  { key: '/help', icon: <QuestionCircleOutlined />, label: 'Помощь' },
  { key: '/bug-reports', icon: <BugOutlined />, label: 'Сообщить о проблеме' },
];

function filterMenuByAccess(items: MenuItem[], plan: string, role: string | undefined): MenuItem[] {
  const level = PLAN_LEVELS[plan] ?? 0;
  return items
    .filter((item) => {
      const minLevel = PLAN_LEVELS[item.minPlan || 'free'] ?? 0;
      return level >= minLevel && hasMinimumRole(role, item.minRole);
    })
    .map((item) => {
      if (item.children) {
        const filtered = filterMenuByAccess(item.children, plan, role);
        if (filtered.length === 0) return null;
        return { ...item, children: filtered };
      }
      return item;
    })
    .filter(Boolean) as MenuItem[];
}

const planColors: Record<string, string> = { free: 'default', starter: 'green', business: 'blue', enterprise: 'gold' };
const planLabels: Record<string, string> = { free: 'СТАРТ', starter: 'РОСТ', business: 'БИЗНЕС', enterprise: 'ПРЕМИУМ' };

// ═══════════════════════════════════════════════
// Применение пользовательской конфигурации меню
// ═══════════════════════════════════════════════

function applyMenuConfig(items: MenuItem[], configs: MenuItemConfig[]): MenuItem[] {
  if (configs.length === 0) return items;
  const itemMap = new Map(items.map(item => [item.key, item]));
  const result: MenuItem[] = [];
  for (const config of configs) {
    const item = itemMap.get(config.key);
    if (!item || !config.visible) { itemMap.delete(config.key); continue; }
    if (item.children && config.children) {
      const childMap = new Map(item.children.map(ch => [ch.key, ch]));
      const orderedChildren = config.children
        .filter(cc => cc.visible && childMap.has(cc.key))
        .map(cc => childMap.get(cc.key)!);
      const configChildKeys = new Set(config.children.map(cc => cc.key));
      for (const ch of item.children) {
        if (!configChildKeys.has(ch.key)) orderedChildren.push(ch);
      }
      if (orderedChildren.length > 0) result.push({ ...item, children: orderedChildren });
    } else {
      result.push(item);
    }
    itemMap.delete(config.key);
  }
  for (const item of itemMap.values()) result.push(item);
  return result;
}

// ═══════════════════════════════════════════════
// Regular Admin Layout
// ═══════════════════════════════════════════════
const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout, admin, tenant, fetchMe, availableTenants } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { mode: themeMode, toggle: toggleTheme } = useThemeStore();

  useEffect(() => {
    if (!admin || !tenant) fetchMe();
  }, []);

  const selectedKey = location.pathname.startsWith('/products/') ? '/products'
    : location.pathname.startsWith('/clients/') && !['/clients/tags', '/clients/imports'].includes(location.pathname) ? '/clients'
      : location.pathname;
  const plan = tenant?.plan || 'free';
  const { configs: menuConfigs, load: loadMenuConfig } = useMenuConfigStore();
  React.useEffect(() => { loadMenuConfig(tenant?.id); }, [tenant?.id]);
  const accessFiltered = filterMenuByAccess(allMenuItems, plan, admin?.role);
  const menuItems = applyMenuConfig(accessFiltered, menuConfigs);
  const activeParent = menuItems.find((item) => item.children?.some((child) => child.key === selectedKey))?.key;
  const activeItem = menuItems.flatMap((item) => item.children || [item]).find((item) => item.key === selectedKey);
  const currentPageLabel = typeof activeItem?.label === 'string' ? activeItem.label : 'Рабочее место';
  const [openKeys, setOpenKeys] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (activeParent) setOpenKeys((keys) => keys.includes(activeParent) ? keys : [...keys, activeParent]);
  }, [activeParent]);

  React.useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const sidebarContent = (compact: boolean) => (
    <div className="admin-sidebar-inner">
      <div className="admin-sidebar-brand">
        {isMobile && <Button type="text" icon={<CloseOutlined />} aria-label="Закрыть меню"
          onClick={() => setMobileMenuOpen(false)} className="admin-sidebar-close" />}
        <span className="admin-brandmark" aria-hidden="true">Ж</span>
        {!compact && <span className="admin-brand-copy"><strong>Жорка</strong><small>Панель ресторана</small></span>}
      </div>
      {!compact && <div className="admin-tenant-card">
        <span>ВАША КОМПАНИЯ</span>
        <strong title={tenant?.name || undefined}>{tenant?.name || 'Компания'}</strong>
        <Tag color={planColors[plan]}>{planLabels[plan] || plan.toUpperCase()}</Tag>
      </div>}
      {!compact && <div className="admin-sidebar-caption">РАБОЧЕЕ МЕСТО</div>}
      <Menu theme="dark" mode="inline" selectedKeys={[selectedKey]} items={toAntMenuItems(menuItems)}
        openKeys={openKeys} onOpenChange={setOpenKeys}
        onClick={({ key }) => { navigate(key); setMobileMenuOpen(false); }}
        className="admin-nav" />
      <div className="admin-sidebar-footer"><MenuConfigurator allMenuItems={accessFiltered} tenantId={tenant?.id} collapsed={compact} /></div>
    </div>
  );

  // Статус подписки для глобального баннера (напоминание об оплате).
  const [billing, setBilling] = React.useState<{ is_active: boolean; days_remaining: number } | null>(null);
  React.useEffect(() => {
    if (!tenant?.id) return;
    api.get('/billing/subscription')
      .then((r) => setBilling({ is_active: r.data?.is_active !== false, days_remaining: r.data?.days_remaining ?? 0 }))
      .catch(() => setBilling(null));
  }, [tenant?.id]);

  const showBillingBanner = billing && (billing.is_active === false || (billing.days_remaining >= 0 && billing.days_remaining <= 5));

  const userMenuItems: MenuProps['items'] = [
    { key: 'role', label: admin?.role === 'owner' ? 'Владелец' : admin?.role || '—', disabled: true },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Выйти', danger: true },
  ];

  return (
    <Layout className="admin-shell" style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer placement="left" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}
          width={288} closable={false} rootClassName="admin-mobile-drawer" styles={{ body: { padding: 0, background: '#25231e' } }}>
          {sidebarContent(false)}
        </Drawer>
      ) : (
        <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={264} className="admin-sidebar" theme="dark">
          {sidebarContent(collapsed)}
        </Sider>
      )}
      <Layout className="admin-main">
        <Header className="admin-topbar" style={{ padding: isMobile ? '0 16px' : '0 32px' }}>
          <Space>
            {isMobile && <Button type="text" icon={<MenuOutlined />} aria-label="Открыть меню"
              onClick={() => setMobileMenuOpen(true)} />}
            {!isMobile && <div className="admin-topbar-location"><span>РАБОЧЕЕ МЕСТО</span><strong>{currentPageLabel}</strong></div>}
            {availableTenants.length > 1 ? (
              <TenantSwitcher compact={isMobile} />
            ) : (
              isMobile && tenant && <Text ellipsis style={{ fontSize: 14, maxWidth: 150, fontWeight: 700 }}>{tenant.name}</Text>
            )}
          </Space>
          <Space size="middle">
            {!isMobile && <Tooltip title={themeMode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}>
              <Switch checked={themeMode === 'dark'} onChange={toggleTheme} checkedChildren="🌙" unCheckedChildren="☀️" size="small" />
            </Tooltip>}
            <Dropdown menu={{ items: userMenuItems, onClick: ({ key }) => { if (key === 'logout') { logout(); navigate('/login'); } } }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar size="small" icon={<UserOutlined />} className="admin-avatar" />
              {!isMobile && <Text style={{ fontSize: 13 }}>{admin?.email || '...'}</Text>}
            </Space>
          </Dropdown>
          </Space>
        </Header>
        <Content className="admin-workspace" style={{ margin: isMobile ? '16px 12px 32px' : '28px 32px 48px', minWidth: 0 }}>
          {showBillingBanner && billing && (
            <Alert
              type={billing.is_active === false ? 'error' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
              message={billing.is_active === false
                ? 'Подписка истекла — приём заказов на витрине приостановлен'
                : `Подписка истекает через ${billing.days_remaining} дн.`}
              description={!hasMinimumRole(admin?.role, 'owner')
                ? 'Сообщите владельцу компании: продлить подписку может только он.'
                : billing.is_active === false
                  ? 'Оплатите подписку, чтобы возобновить приём заказов.'
                  : 'Продлите подписку заранее, чтобы работа не прерывалась.'}
              action={hasMinimumRole(admin?.role, 'owner') ? (
                <Button size="small" type="primary" onClick={() => navigate('/billing')}>
                  Перейти к оплате
                </Button>
              ) : undefined}
            />
          )}
          {<ErrorBoundary>{children}</ErrorBoundary>}
        </Content>
      </Layout>
    </Layout>
  );
};

// ═══════════════════════════════════════════════
// Super-Admin Layout (gold theme)
// ═══════════════════════════════════════════════
const saMenuItems = [
  { key: '/superadmin', icon: <DashboardOutlined />, label: 'Обзор' },
  { key: '/superadmin/tenants', icon: <ShopOutlined />, label: 'Тенанты' },
  { key: '/superadmin/revenue', icon: <BarChartOutlined />, label: 'Выручка' },
];

const SALayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout, name } = useSAStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);
  const { token } = theme.useToken();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={240}
        style={{ background: '#1a1a2e' }}
        theme="dark"
      >
        <div style={{ padding: '16px', textAlign: 'center' }}>
          {collapsed ? (
            <CrownOutlined style={{ color: '#faad14', fontSize: 24 }} />
          ) : (
            <>
              <CrownOutlined style={{ color: '#faad14', fontSize: 28 }} />
              <Title level={5} style={{ color: '#faad14', margin: '4px 0 0' }}>
                Super Admin
              </Title>
            </>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={saMenuItems}
          onClick={({ key }) => navigate(key)}
          style={{ background: '#1a1a2e' }}
        />
      </Sider>
      <Layout>
        <Header style={{ background: token.colorBgContainer, padding: '0 24px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
          <Space>
            <Tag color="gold"><CrownOutlined /> Super Admin</Tag>
          </Space>
          <Dropdown menu={{
            items: [
              { key: 'name', label: name || 'Admin', disabled: true },
              { type: 'divider' },
              { key: 'logout', icon: <LogoutOutlined />, label: 'Выйти', danger: true },
            ],
            onClick: ({ key }) => { if (key === 'logout') { logout(); navigate('/superadmin/login'); } }
          }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar size="small" icon={<CrownOutlined />} style={{ backgroundColor: '#faad14' }} />
              <Text style={{ fontSize: 13 }}>{name || 'Super Admin'}</Text>
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ margin: 24, padding: 24, background: token.colorBgContainer, borderRadius: 8 }}>
          {<ErrorBoundary>{children}</ErrorBoundary>}
        </Content>
      </Layout>
    </Layout>
  );
};

// ═══════════════════════════════════════════════
// Route guards
// ═══════════════════════════════════════════════

/** Полная защита — авторизация + наличие тенанта */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const needsOnboarding = useAuthStore((s) => s.needsOnboarding);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
};

/** Только авторизация — для страницы онбординга (без проверки тенанта) */
const AuthOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const SAProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSAStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/superadmin/login" replace />;
  return <>{children}</>;
};


// Fallback для lazy-загрузки полностраничных компонентов
const FullPageLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <Spin size="large" />
  </div>
);

// ═══════════════════════════════════════════════
// App
// ═══════════════════════════════════════════════
const App: React.FC = () => {
  const [authChecked, setAuthChecked] = React.useState(false);
  const themeMode = useThemeStore((s) => s.mode);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  React.useEffect(() => {
    // Проверяем сессию по cookies при загрузке приложения
    useAuthStore.getState().checkAuth().finally(() => setAuthChecked(true));
  }, []);

  if (!authChecked) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
        background: themeMode === 'dark' ? '#141414' : '#fff', transition: 'background 0.3s' }}>
        <Spin size="large" tip="Загрузка..." />
      </div>
    );
  }

  return (
    <ConfigProvider theme={{ algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: { colorPrimary: '#f05a28', borderRadius: 10, fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif',
        colorBgLayout: themeMode === 'dark' ? '#171714' : '#f7f3ec', colorBgContainer: themeMode === 'dark' ? '#24231f' : '#fffaf2' } }}>
      <BrowserRouter basename="/admin">
        <Routes>
          {/* Публичные страницы */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Приглашение — полупубличная страница (может быть без авторизации) */}
          <Route path="/invite/:token" element={
            <React.Suspense fallback={<FullPageLoading />}>
              <AcceptInvitePage />
            </React.Suspense>
          } />

          {/* Онбординг — только авторизация, без проверки наличия тенанта */}
          <Route path="/onboarding" element={
            <AuthOnlyRoute>
              <React.Suspense fallback={<FullPageLoading />}>
                <OnboardingPage />
              </React.Suspense>
            </AuthOnlyRoute>
          } />

          {/* Защищённые страницы — авторизация + тенант */}
          <Route path="/*" element={
            <ProtectedRoute>
              <AdminLayout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/products/:id" element={<ProductDetailPage />} />
                  <Route path="/clients/:id" element={<ClientProfilePage />} />
                  {/* Все страницы из единого реестра */}
                  {PAGES.filter(p => p.path !== '/').map(p => (
                    <Route key={p.path} path={p.path} element={
                      <RoleRoute minRole={p.minRole || 'operator'}><ErrorBoundary><LazyLoadErrorBoundary>
                        <React.Suspense fallback={<div>Загрузка...</div>}>
                          <p.component />
                        </React.Suspense>
                      </LazyLoadErrorBoundary></ErrorBoundary></RoleRoute>
                    } />
                  ))}
                  <Route path="/settings/employees" element={<Navigate to="/settings/team" replace />} />
                  <Route path="/help" element={<HelpCenterPage />} />
                  <Route path="/billing" element={<RoleRoute minRole="owner"><BillingPage /></RoleRoute>} />
                </Routes>
              </AdminLayout>
            </ProtectedRoute>
          } />

          {/* Super-admin */}
          <Route path="/superadmin/login" element={<SALoginPage />} />
          <Route path="/superadmin/*" element={
            <SAProtectedRoute>
              <SALayout>
                <Routes>
                  <Route path="/" element={<SADashboardPage />} />
                  <Route path="/tenants" element={<SATenantsPage />} />
                  <Route path="/revenue" element={<SARevenuePage />} />
                </Routes>
              </SALayout>
            </SAProtectedRoute>
          } />
        </Routes>
              <AdminBugReportWidget />
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;
